import { useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store';
import { selectChartArms, selectBioequivalence } from '@/store/selectors';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { ScreenEmpty } from '@/components/ui/ScreenEmpty';
import { formatNumber } from '@/utils/format';

/**
 * Charts screen — rich concentration-time visualization + BE forest plot.
 */
export function ChartsScreen() {
  const arms = useStore(useShallow(selectChartArms));
  const setActiveScreen = useStore((s) => s.setActiveScreen);
  const requestLoadDemo = useStore((s) => s.requestLoadDemo);
  const [logScale, setLogScale] = useState(false);
  const [showAuc, setShowAuc] = useState(true);

  const hasData = arms.some((a) => a.points.length > 0);

  if (!hasData) {
    return (
      <div className="charts-screen">
        <div className="charts-main">
          <ScreenEmpty
            title="No data to plot"
            body="Enter concentration-time data on the Calculator to visualize the curve."
            primary={{ label: 'Open Calculator', onClick: () => setActiveScreen('calculator') }}
            secondary={{ label: 'Load Demo', onClick: requestLoadDemo }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="charts-screen">
      <div className="charts-main">
        <ConcentrationTimeChart arms={arms} logScale={logScale} showAuc={showAuc} />
      </div>
      <div className="charts-controls">
        <CollapsibleSection sectionKey="charts.controls" title="Plot Controls" column="single" defaultOpen>
          <div className="flex-col-gap-2">
            <label className="checkbox-row" title="Linear is best for absorption/peak; log reveals the terminal elimination slope.">
              <input type="checkbox" checked={logScale} onChange={(e) => setLogScale(e.target.checked)} />
              <span>Log-scale Y axis</span>
            </label>
            <label className="checkbox-row" title="Shades the trapezoidal AUC(0-t) area under each curve.">
              <input type="checkbox" checked={showAuc} onChange={(e) => setShowAuc(e.target.checked)} />
              <span>Shade AUC area</span>
            </label>
          </div>
        </CollapsibleSection>
        <CollapsibleSection sectionKey="charts.forest" title="Forest-Plot Controls" column="single">
          <ForestPlot />
        </CollapsibleSection>
      </div>
    </div>
  );
}

interface ChartArmData {
  armId: string;
  label: string;
  role: string;
  color: string;
  points: { time: number; concentration: number }[];
}

function ConcentrationTimeChart({
  arms,
  logScale,
  showAuc,
}: {
  arms: ChartArmData[];
  logScale: boolean;
  showAuc: boolean;
}) {
  const W = 720;
  const H = 420;
  const padL = 50;
  const padR = 20;
  const padT = 20;
  const padB = 40;

  const { paths, aucPaths, axes } = useMemo(() => {
    const allPoints = arms.flatMap((a) => a.points);
    if (allPoints.length === 0) {
      return { paths: [], aucPaths: [], axes: null };
    }

    const maxT = Math.max(...allPoints.map((p) => p.time));
    const maxC = Math.max(...allPoints.map((p) => p.concentration));

    const xScale = (t: number) => padL + (t / maxT) * (W - padL - padR);
    const yScale = (c: number) => {
      if (logScale && c <= 0) return H - padB;
      const val = logScale
        ? Math.log(c) / Math.log(maxC)
        : c / maxC;
      return H - padB - val * (H - padT - padB);
    };

    const linePaths = arms
      .filter((a) => a.points.length > 0)
      .map((arm) => {
        const d = arm.points
          .map((p, i) => {
            const x = xScale(p.time);
            const y = yScale(p.concentration);
            return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
          })
          .join(' ');
        const areaD =
          `${d} L${xScale(arm.points[arm.points.length - 1]!.time).toFixed(1)},${(H - padB).toFixed(1)}` +
          ` L${xScale(arm.points[0]!.time).toFixed(1)},${(H - padB).toFixed(1)} Z`;
        return { color: arm.color, d, areaD, label: arm.label };
      });

    const aucAreaPaths = showAuc
      ? linePaths.map((p) => ({ ...p, d: p.areaD }))
      : [];

    const ticks = {
      x: Array.from({ length: 6 }, (_, i) => (maxT / 5) * i),
      y: logScale
        ? []
        : Array.from({ length: 5 }, (_, i) => (maxC / 4) * i),
    };

    return { paths: linePaths, aucPaths: aucAreaPaths, axes: { ticks, xScale, yScale, maxT, maxC } };
  }, [arms, logScale, showAuc]);

  if (paths.length === 0) {
    return (
      <div className="chart-empty">
        <p className="empty-state">No data to plot. Enter concentration-time data on the Calculator.</p>
      </div>
    );
  }

  return (
    <div className="chart-figure">
    <svg viewBox={`0 0 ${W} ${H}`} className="chart-svg" role="img" aria-label="Concentration-time chart">
      {}
      {axes && !logScale
        ? axes.ticks.y.map((v, i) => {
            const y = axes.yScale(v);
            return (
              <g key={`y${i}`}>
                <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="var(--color-border)" strokeWidth={0.5} />
                <text x={padL - 5} y={y + 4} textAnchor="end" fontSize={10} fill="var(--color-text-muted)">
                  {formatNumber(v, 2)}
                </text>
              </g>
            );
          })
        : null}

      {}
      {axes
        ? axes.ticks.x.map((v, i) => {
            const x = axes.xScale(v);
            return (
              <g key={`x${i}`}>
                <line x1={x} y1={H - padB} x2={x} y2={H - padB + 4} stroke="var(--color-border)" strokeWidth={0.5} />
                <text x={x} y={H - padB + 16} textAnchor="middle" fontSize={10} fill="var(--color-text-muted)">
                  {formatNumber(v, 1)}
                </text>
              </g>
            );
          })
        : null}

      {}
      <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke="var(--color-border)" strokeWidth={1} />
      <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="var(--color-border)" strokeWidth={1} />

      {}
      {aucPaths.map((p, i) => (
        <path key={`auc${i}`} d={p.d} fill={p.color} opacity={0.1} stroke="none" />
      ))}

      {}
      {paths.map((p, i) => (
        <g key={`line${i}`}>
          <path d={p.d} fill="none" stroke={p.color} strokeWidth={2} />
          {arms[i]!.points.map((pt, j) => (
            <circle
              key={j}
              cx={axes!.xScale(pt.time)}
              cy={axes!.yScale(pt.concentration)}
              r={3}
              fill={p.color}
            />
          ))}
        </g>
      ))}

      {}
      <g>
        {paths.map((p, i) => (
          <g key={`leg${i}`}>
            <line
              x1={W - padR - 120}
              y1={padT + 10 + i * 16}
              x2={W - padR - 105}
              y2={padT + 10 + i * 16}
              stroke={p.color}
              strokeWidth={2}
            />
            <text x={W - padR - 100} y={padT + 14 + i * 16} fontSize={11} fill="var(--color-text)">
              {p.label}
            </text>
          </g>
        ))}
      </g>
    </svg>
    <p className="field-hint">
      Concentration (Y) vs time (X) per arm. Points are observed samples; the line is the
      trapezoidal interpolation used for AUC. The shaded region is AUC(0-t).
    </p>
    </div>
  );
}

function ForestPlot() {
  const be = useStore(selectBioequivalence);
  const sigFigs = useStore((s) => s.settings.significantFigures);

  if (!be) {
    return <p className="empty-state">No bioequivalence data. Run a multi-subject analysis first.</p>;
  }

  const W = 360;
  const H = 140;
  const padL = 60;
  const padR = 20;
  const lo = 60;
  const hi = 140;
  const xScale = (v: number) => padL + ((v - lo) / (hi - lo)) * (W - padL - padR);

  const rows = [
    { label: 'AUC', gmr: be.aucGmr * 100, lower: be.aucCI90.lower * 100, upper: be.aucCI90.upper * 100, pass: be.aucPass },
    { label: 'Cmax', gmr: be.cmaxGmr * 100, lower: be.cmaxCI90.lower * 100, upper: be.cmaxCI90.upper * 100, pass: be.cmaxPass },
  ];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Forest plot">
      {}
      {[80, 100, 125].map((v) => (
        <g key={v}>
          <line
            x1={xScale(v)}
            y1={20}
            x2={xScale(v)}
            y2={H - 30}
            stroke={v === 100 ? 'var(--color-border)' : 'var(--color-border-light)'}
            strokeWidth={v === 100 ? 1 : 0.5}
            strokeDasharray={v === 100 ? 'none' : '4,2'}
          />
          <text x={xScale(v)} y={H - 15} textAnchor="middle" fontSize={9} fill="var(--color-text-muted)">
            {v}%
          </text>
        </g>
      ))}

      {}
      <rect x={xScale(80)} y={20} width={xScale(125) - xScale(80)} height={H - 50} fill="var(--color-success)" opacity={0.08} />

      {rows.map((r, i) => {
        const y = 40 + i * 35;
        return (
          <g key={r.label}>
            <text x={padL - 5} y={y + 4} textAnchor="end" fontSize={10} fill="var(--color-text)">
              {r.label}
            </text>
            <line
              x1={xScale(r.lower)}
              y1={y}
              x2={xScale(r.upper)}
              y2={y}
              stroke={r.pass ? 'var(--color-success)' : 'var(--color-danger)'}
              strokeWidth={2}
            />
            <circle cx={xScale(r.gmr)} cy={y} r={4} fill={r.pass ? 'var(--color-success)' : 'var(--color-danger)'} />
            <text x={xScale(r.upper) + 8} y={y + 4} fontSize={9} fill="var(--color-text-muted)">
              [{formatNumber(r.lower, sigFigs)}–{formatNumber(r.upper, sigFigs)}]
            </text>
          </g>
        );
      })}
    </svg>
  );
}
